import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Box, IconButton, Stack, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { Calendar as ReactCalendar, CalendarProps } from 'react-big-calendar';
import 'react-big-calendar/lib/sass/styles.scss';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.scss';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar, closeSnackbar, SnackbarKey } from 'notistack';

import { Filter as FilterIcon, NewSearch as SearchIcon, Notice, NoticeStack } from '@linetweet/linetweet-ui';

import { DayjsLocalizer, isArrayEqual, TimeHelper } from 'utils';
import { CalendarEventTypeEnum, Employee, Shift, Service } from 'types';

import { LocaleSelectContext } from 'layouts/LocaleProvider/LocaleProvider';

import { setTags } from 'utils/sentry';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getCalendarSlots } from '../../store/calendarSlots/thunks';
import { fetchCalendarEvents } from '../../store/appointments/thunks';
import { fetchEmployees, getEmployeesByIds } from '../../store/employees/thunks';
import { joinStore, listenDateChanges } from '../../store/global/thunks';
import { ConnectionType } from '../../store/global/slice';
import { fetchPeakTimes } from '../../store/peakTimes/thunks';
import { getEventStats } from '../../store/eventStats/thunks';
import { selectCurrentStore, selectStoreServices } from '../../store/store/selectors';

import { buildAppointmentEvents } from '../commons/services/appointmentsCalendarService';
import { convertDaySummaryToCalendarDataType } from '../commons/services/calendarOverviewService';
import { AppointmentFiltersLocalstorageSchema } from '../CalendarEvents/util/appointmentFiltersSchema';

import { AppointmentFilters, CalendarEvent } from './types';
import {
  AppointmentsFilters,
  CreateAppointmentButton,
  DatePicker,
  DayEvent,
  DayOverview,
  DayView,
  ResourceHeader,
  RoomsAmountValues,
  SeriesButton,
  TimeSlotWrapper,
  EventWrapper,
} from './components';
import { CalendarFooter, EventContainerWrapper, TimeGutterHeader } from './subcomponents';

import { getMissingShiftEmployeeIds } from '../../store/employees/selectors';
import { selectDaySlotsRecalculating } from '../../store/global/selectors';

import { getDayViewTimeRange } from './utils';
import { CALENDAR_FILTERS_LOCAL_STORAGE } from './constants';

import styles from './AppointmentsCalendar.module.scss';

export type AppointmentsCalendarContainerProps = {
  onAppointmentClick: (params: { calendarEvent: CalendarEvent; isPastEvent: boolean }) => void;
  onCreateAppointmentClick: (params?: {
    services: Service[];
    time: number;
    date: string;
    duration: number;
    employeeId?: string;
    isSplit?: boolean;
  }) => void;
  date: Dayjs;
  onDateChange: (date: string) => void;
  onShowCalendarOverviewClick: () => void;
  isReportEnabled: boolean;
  onShowReportClick: () => void;
};

const defaultFilters: AppointmentFilters = {
  focus: [],
  employee: null,
  workingHours: {
    from: 8 * 60,
    to: 20 * 60,
  },
  services: [],
};

const getFiltersInitialState = (timezone: string): AppointmentFilters => {
  const localStorageValue = localStorage.getItem(CALENDAR_FILTERS_LOCAL_STORAGE);

  if (localStorageValue) {
    try {
      const filtersData = JSON.parse(localStorageValue);

      if (!filtersData) {
        return defaultFilters;
      }

      AppointmentFiltersLocalstorageSchema.validateSync(filtersData);

      const startOfDayTimestamp = TimeHelper.toDayjs(Date.now(), timezone).startOf('day').valueOf();

      if (startOfDayTimestamp > filtersData.createdAt) {
        localStorage.removeItem(CALENDAR_FILTERS_LOCAL_STORAGE);
        return defaultFilters;
      }

      return filtersData.values;
    } catch (error) {
      localStorage.removeItem(CALENDAR_FILTERS_LOCAL_STORAGE);
    }
  }

  return defaultFilters;
};

export function AppointmentsCalendar({
  onAppointmentClick,
  onCreateAppointmentClick,
  date,
  onDateChange,
  onShowCalendarOverviewClick,
  isReportEnabled,
  onShowReportClick,
}: AppointmentsCalendarContainerProps) {
  const intl = useIntl();

  const dateString = useMemo(() => TimeHelper.toStandardFormat(date), [date]);

  const appointmentsState = useAppSelector((state) => state.appointments);
  const employeesState = useAppSelector((state) => state.employees.storeEmployees);
  const store = useAppSelector(selectCurrentStore, shallowEqual);
  const isSeriesEnabled = Boolean(store.settings?.series?.enabled);
  const peakTimesState = useAppSelector((state) => state.peakTimes);
  const calendarSlotsState = useAppSelector((state) => state.calendarSlots);
  const globalState = useAppSelector((state) => state.global);
  const eventStatsState = useAppSelector((state) => state.eventStats);
  const allServices = useAppSelector(selectStoreServices, shallowEqual);
  const daySlotsRecalculating = useAppSelector((state) => selectDaySlotsRecalculating(state, dateString), shallowEqual);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [view, setView] = useState<'day' | 'week'>('day');
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [offlineEvents, setOfflineEvents] = useState<CalendarEvent[]>([]);
  const [workingHours, setWorkingHours] = useState({ isLoading: true }) as any;
  const [isFiltersOpened, setIsFiltersOpened] = useState(false);
  const [filters, setFilters] = useState<AppointmentFilters>(() => getFiltersInitialState(store.timezone));
  const [deletionWarningState, setDeletionWarningState] = useState<'visible' | 'disabled' | 'hidden'>('hidden');
  const [deletionErrorState, setDeletionErrorState] = useState<boolean>(false);
  const [slotsErrorShown, setSlotsErrorShown] = useState(false);
  const [calendarData, setCalendarData] = useState<any>({ events: [], resources: [], eventTypeCounters: {}, loading: true });
  const [timer, setTimer] = useState(TimeHelper.convertToMinutesFromDayStart(TimeHelper.toDayjs(Date.now(), store.timezone)));

  const localeContext = useContext(LocaleSelectContext);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const missingEmployeeIds = useAppSelector((state) => getMissingShiftEmployeeIds(state, dateString));

  const isFiltersActive = useMemo(() => !isArrayEqual(filters, defaultFilters), [filters]);

  // Treat employees as "loading" only on the initial load. Once we have any
  // employees in the store, background re-fetches (e.g. modal date changes)
  // shouldn't blank the calendar.
  const employeesInitialLoading = employeesState.loading && employeesState.data.employees.length === 0;

  const notLoading = useMemo(
    () => !appointmentsState.loading && !employeesInitialLoading && !calendarSlotsState.loading,
    [appointmentsState.loading, employeesInitialLoading, calendarSlotsState.loading],
  );

  useEffect(() => {
    if (globalState.connectionStatus === ConnectionType.Connected) {
      dispatch(listenDateChanges(dateString));
    }
  }, [dateString, dispatch, globalState.connectionStatus, store.id]);

  useEffect(() => {
    if (globalState.connectionStatus === ConnectionType.Connected) {
      dispatch(joinStore());
    }
  }, [dispatch, globalState.connectionStatus]);

  useEffect(() => {
    if (date.valueOf() !== TimeHelper.toDayjs(Date.now(), store.timezone).startOf('day').valueOf()) {
      return;
    }
    const intervalId = setInterval(() => {
      const updatedValue = timer + 1;
      setTimer(updatedValue);
    }, 60000);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(intervalId);
  }, [timer, dateString]);

  useEffect(() => {
    if (appointmentsState.deleteCalendarEvent.error) {
      setDeletionErrorState(true);
      setDeletionWarningState('hidden');
    } else {
      setDeletionErrorState(false);
      if (appointmentsState.deleteCalendarEvent.loading) {
        if (deletionWarningState === 'hidden') {
          setDeletionWarningState('visible');
        }
      } else {
        setDeletionWarningState('hidden');
      }
    }
  }, [appointmentsState, deletionWarningState]);

  useEffect(() => {
    if (store.hours) {
      let storeHours = store.hours.specialDates.find((workingDate) => workingDate.date === dateString);
      if (!storeHours) {
        const selectedWeekDay = date.day();
        storeHours = store.hours.regular.find((workingDay) => workingDay.weekday === selectedWeekDay);
      }
      if (workingHours !== storeHours) {
        setWorkingHours(storeHours);
      }
    }

    setTags({ storeId: store.id });

    if (!eventStatsState.data || !eventStatsState.data[dateString]) {
      if (eventStatsState.loading) {
        return;
      }
      dispatch(
        getEventStats({
          from: date.startOf('day').valueOf(),
          to: date.endOf('day').valueOf(),
          storeId: store.id,
          enterpriseId: store.enterpriseId,
        }),
      );
    }
  }, [store, dateString]);

  useEffect(() => {
    const from = view === 'day' ? date.startOf('day') : date.startOf('week');
    const to = from.add(1, view);
    const { id: storeId, enterpriseId, timezone } = store;

    dispatch(getCalendarSlots({ date: dateString, timestamp: Date.now() }));
    dispatch(fetchCalendarEvents({ from, to, timezone }));
    dispatch(fetchEmployees({ from, timezone: store.timezone, ...(view === 'week' && { to }) }));
    dispatch(fetchPeakTimes({ date: from, timezone, storeId, enterpriseId }));
  }, [store, dateString, view]);

  useEffect(() => {
    setSlotsErrorShown(Boolean(calendarSlotsState.error));
  }, [calendarSlotsState.error]);

  const employeeNameById = useMemo(() => {
    if (employeesState.data.employees.length < 1) {
      return {};
    }
    return employeesState.data.employees.reduce((acc, employee: Employee) => {
      acc[employee.id] = `${employee.firstName} ${employee.lastName}`;
      return acc;
    }, {});
  }, [employeesState.data.employees]);

  useEffect(() => {
    if (employeesState.loading || employeesState.error || !workingHours.isOpen) {
      return;
    }
    const dateStr = TimeHelper.toStandardFormat(date);
    if (!employeesState.data.shifts[dateStr] || employeesState.data.shifts[dateStr].length < 1) {
      return;
    }
    if (isArrayEqual(employeesState.data.shifts[dateStr], shifts) && !filters.employee && filters.services.length < 1 && !filters.focus) {
      return;
    }
    const employeeIdsByFocusMap: Record<string, string[]> = appointmentsState.data.reduce((acc, appointment) => {
      if (appointment.type === CalendarEventTypeEnum.APPOINTMENT) {
        return acc;
      }

      if (!appointment.type) {
        return acc;
      }
      if (appointment.date !== dateStr) {
        return acc;
      }
      // @ts-ignore
      let employeeId = appointment.pinnedEmployee;
      // @ts-ignore
      if (appointment.eventTypeData && appointment.eventTypeData.suggestedEmployee) {
        // @ts-ignore
        employeeId = appointment.eventTypeData.suggestedEmployee;
      }
      if (!employeeId || employeeId === 'missed') {
        return acc;
      }
      const focusKey = appointment.type.toString();
      if (!acc[focusKey]) {
        acc[focusKey] = [];
      }
      acc[focusKey].push(employeeId);
      return acc;
    }, {});

    employeeIdsByFocusMap[CalendarEventTypeEnum.APPOINTMENT] = employeesState.data.employees.map((employee) => employee.id);

    let focusValues = filters.focus;
    if (filters.services.length) {
      focusValues = filters.focus.filter((focusValue) => focusValue !== CalendarEventTypeEnum.APPOINTMENT);
    }

    const filterByFocus = !!focusValues.length && appointmentsState.data.length > 0;

    const filteredShifts = employeesState.data.shifts[dateStr].filter((shift: Shift) => {
      if (!focusValues.length && !filters.services.length) {
        return true;
      }

      let focusMatch = false;
      if (filterByFocus) {
        focusMatch = !!focusValues.find((focusValue) => employeeIdsByFocusMap[focusValue]?.includes(shift.employeeId));
      }

      let servicesMatch = false;
      if (filters.services.length) {
        servicesMatch = !!filters.services.find((service) => shift.services.includes(service.value));
      }

      return focusMatch || servicesMatch;
    });
    const offlineEvent = [] as CalendarEvent[];
    const dateStartTimestamp = date.endOf('day').subtract(24, 'hours').valueOf();
    const dateEndTimestamp = date.endOf('day').valueOf() - 1;
    const weekday = workingHours.weekday || date.isoWeekday();
    filteredShifts.forEach((shift) => {
      const fromTimestamp = shift.start - 3 * 60000;
      const fromMinutes = Math.floor((fromTimestamp - dateStartTimestamp) / 60000);

      const toTimestamp = shift.end + 3 * 60000;
      const toMinutes = Math.floor((toTimestamp - dateStartTimestamp) / 60000);

      const duration = toMinutes - fromMinutes;

      const startDate = TimeHelper.timestampTzToLocal(fromTimestamp, store.timezone);
      const endDate = TimeHelper.timestampTzToLocal(toTimestamp, store.timezone);

      if (fromTimestamp >= dateStartTimestamp && fromTimestamp <= dateEndTimestamp) {
        const untilEndOfDay = workingHours.to === toMinutes;
        offlineEvent.push({
          id: `working-range-${shift.employeeId}-${weekday}`,
          start: new Date(startDate),
          startFormatted: TimeHelper.getHHmmTimeFromMinutes(fromMinutes),
          duration,
          end: new Date(endDate),
          endFormatted: TimeHelper.getHHmmTimeFromMinutes(toMinutes),
          type: CalendarEventTypeEnum.WORKING_HOURS,
          resourceId: shift.employeeId,
          isWorkingRange: true,
          untilEndOfDay,
          services: [],
          splitService: false,
        });
      }
    });
    setOfflineEvents(offlineEvent);
    setShifts(filteredShifts);
  }, [employeesState.data.shifts, employeesState.data.employees, appointmentsState.data, workingHours, store, filters, dateString]);

  const roomsAmount = useMemo<RoomsAmountValues | null>(() => {
    if (!store.resources || store.resources.length < 1) {
      return null;
    }
    // TODO: We change state on component?
    // TODO: We should trust data from our servers, if resource is there it should have a name and count
    return store.resources.reduce((acc, resource) => {
      if (resource.resource?.name) {
        acc[resource.resource.name] = resource.count || 0;

        const specialDateOverride = resource.specialDates?.find((specialDate) => specialDate.date === dateString);

        if (specialDateOverride) {
          acc[resource.resource.name] = specialDateOverride.count || 0;
        }
      }
      return acc;
    }, {});
  }, [store, dateString]);

  const onDeletionWarningClose = useCallback(() => {
    setDeletionWarningState('disabled');
  }, []);

  const onDeletionErrorClose = useCallback(() => {
    setDeletionErrorState(false);
  }, []);

  const onToggleFilters = useCallback(() => {
    setIsFiltersOpened(!isFiltersOpened);
  }, [isFiltersOpened]);

  const onSaveFilters = useCallback((values: AppointmentFilters) => {
    setFilters(values);
    localStorage.setItem(CALENDAR_FILTERS_LOCAL_STORAGE, JSON.stringify({
      createdAt: TimeHelper.toDayjs(Date.now(), store.timezone).valueOf(),
      values,
    }));
  }, [store]);

  const onToggleSearch = useCallback(() => {
    navigate('/search');
  }, []);

  useEffect(() => {
    if (!employeesState.loading && missingEmployeeIds.length) {
      dispatch(getEmployeesByIds(missingEmployeeIds));
    }
  }, [missingEmployeeIds, employeesState.loading, dateString]);

  const peakTimes = useMemo(() => {
    if (!peakTimesState.data) {
      return [];
    }
    if (!workingHours) {
      return peakTimesState.data;
    }
    return peakTimesState.data.filter((item) => {
      const start = TimeHelper.getMinutesFromHHmmTimeString(item.start);
      const end = TimeHelper.getMinutesFromHHmmTimeString(item.end);
      if (end <= workingHours.from) {
        // earlier than working horus
        return false;
      }
      if (start >= workingHours.to) {
        // later than working hours
        return false;
      }
      return true;
    });
  }, [peakTimesState.data, workingHours]);

  const daySummary = useMemo(() => {
    const dateStr = TimeHelper.toStandardFormat(date);
    if (!eventStatsState.data || !eventStatsState.data[dateStr]) {
      return null;
    }

    const selectedServiceIds = filters.services.map(({ value }) => value);

    return convertDaySummaryToCalendarDataType(eventStatsState.data[dateStr], allServices, selectedServiceIds);
  }, [date, eventStatsState.data, filters.services]);

  useEffect(() => {
    if (!workingHours.isLoading && !workingHours.isOpen) {
      setCalendarData({
        events: [{ loading: true }],
        resources: [{ loading: true }, { loading: true }, { loading: true }, { loading: true }, { loading: true }],
        loading: false,
      });
      return;
    }
    if (employeesInitialLoading || appointmentsState.loading || calendarSlotsState.loading) {
      setCalendarData({
        events: [{ loading: true }],
        resources: [{ loading: true }, { loading: true }, { loading: true }, { loading: true }, { loading: true }],
        loading: true,
      });

      return;
    }
    if (appointmentsState.data.length === 0 && Object.values(calendarSlotsState.data).length === 0) {
      setCalendarData({
        events: [{ loading: true }],
        resources: [{ loading: true }, { loading: true }, { loading: true }, { loading: true }, { loading: true }],
        loading: true,
      });

      return;
    }
    const buildedCalendarData = buildAppointmentEvents(
      appointmentsState.data,
      shifts || [],
      employeeNameById,
      allServices,
      date,
      calendarSlotsState.data,
      store.timezone,
      {
        male: intl.formatMessage({ id: 'common.gender.male' }),
        female: intl.formatMessage({ id: 'common.gender.female' }),
        overflow: intl.formatMessage({ id: 'calendar.overflow' }),
      },
      filters,
    );
    setCalendarData({ ...buildedCalendarData, loading: false });
  }, [
    appointmentsState,
    employeesInitialLoading,
    employeesState,
    shifts,
    date,
    timer,
    allServices,
    calendarSlotsState,
    filters,
    workingHours,
    employeeNameById,
    intl,
    store.timezone,
  ]);

  const slotsRecalculatingSnackbarData = useRef<{ snackbarKey: SnackbarKey; dateString: string } | null>(null);
  useEffect(() => {
    if (
      (!daySlotsRecalculating && slotsRecalculatingSnackbarData.current) ||
      (slotsRecalculatingSnackbarData.current && slotsRecalculatingSnackbarData.current.dateString !== dateString)
    ) {
      closeSnackbar(slotsRecalculatingSnackbarData.current.snackbarKey);
      slotsRecalculatingSnackbarData.current = null;
    }

    if (daySlotsRecalculating && !slotsRecalculatingSnackbarData.current) {
      const snackbarKey = enqueueSnackbar(intl.formatMessage({ id: 'slots.recalculationInProgress' }, { dateString }), {
        variant: 'warning',
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'top',
        },
        persist: true,
        preventDuplicate: true,
        style: {
          backgroundColor: '#ffdc78cc',
          color: '#000000',
        },
      });

      slotsRecalculatingSnackbarData.current = { snackbarKey, dateString };
    }
  }, [daySlotsRecalculating, dateString, intl]);

  useEffect(() => (() => {
    if (slotsRecalculatingSnackbarData.current) {
      closeSnackbar(slotsRecalculatingSnackbarData.current.snackbarKey);
      slotsRecalculatingSnackbarData.current = null;
    }
  }), []);

  const config = useMemo((): CalendarProps => {
    const startOfDay = TimeHelper.getStartOfDay(date.valueOf(), store.timezone);
    const dateLocal = new Date(TimeHelper.getStartOfDayLocal(TimeHelper.getDateStringTz(date.valueOf(), store.timezone)));
    const timeRange = getDayViewTimeRange(startOfDay, calendarData.events, workingHours, store.timezone);
    const getNow = () => TimeHelper.getNowDate(store.timezone);

    return {
      min: timeRange.min,
      max: timeRange.max,
      localizer: DayjsLocalizer(dayjs),
      formats: {
        timeGutterFormat: 'HH:mm',
        dayFormat: 'ddd DD',
      },
      toolbar: false,
      components: {
        timeGutterHeader: TimeGutterHeader,
        resourceHeader: ResourceHeader as any,
        timeSlotWrapper: TimeSlotWrapper.bind({}, peakTimes, calendarData.loading),
        eventWrapper: EventWrapper as any,
        eventContainerWrapper: EventContainerWrapper,
        day: {
          event: DayEvent as any,
        },
        header: view === 'day' ? () => <></> : undefined,
      },
      step: 30,
      timeslots: 2,
      view,
      views: {
        day: DayView,
        // week: WeekView,
      },
      onView: () => {},
      onNavigate: () => {},
      onSelectSlot: () => {},
      onSelectEvent: (event: any) => {
        const eventBase = event as any;
        const appointmentType = (event as CalendarEvent).type;
        const { isProtected } = event;
        if (
          isProtected ||
          [CalendarEventTypeEnum.WALKIN, CalendarEventTypeEnum.RESERVATION, CalendarEventTypeEnum.WORKING_HOURS].includes(appointmentType)
        ) {
          return;
        }
        let appointmentStart = eventBase.start.getTime();
        let appointmentServices = eventBase.services;
        let appointmentTime = eventBase.time;
        let appointmentDuration = eventBase.duration;

        if (eventBase.relatedSlotId) {
          const allRelatedSlots = calendarData.events.filter((e) => e.relatedSlotId === eventBase.relatedSlotId);

          const relatedServices: Service[] = [];

          appointmentDuration = 0;

          for (let allRelatedSlotsIndex = 0; allRelatedSlotsIndex < allRelatedSlots.length; allRelatedSlotsIndex += 1) {
            const relatedSlot = allRelatedSlots[allRelatedSlotsIndex];
            for (let servicesIndex = 0; servicesIndex < relatedSlot.services.length; servicesIndex += 1) {
              if (!relatedServices.find((service) => service.id === relatedSlot.services[servicesIndex].id)) {
                relatedServices.push(relatedSlot.services[servicesIndex]);
              }
            }
            appointmentTime = Math.min(appointmentTime, relatedSlot.time);
            appointmentStart = Math.min(appointmentStart, relatedSlot.start.getTime());
            appointmentDuration += relatedSlot.duration;
          }

          appointmentServices = Array.from(relatedServices);
        }

        const currentTime = getNow();
        const appointmentEnd = appointmentStart + appointmentDuration * 60 * 1000;
        const isPastEvent = appointmentEnd <= currentTime.valueOf();

        if (appointmentType === CalendarEventTypeEnum.SLOT) {
          if (!isPastEvent) {
            onCreateAppointmentClick({
              services: appointmentServices,
              time: appointmentTime,
              date: eventBase.date,
              duration: appointmentDuration,
              isSplit: !!eventBase.relatedSlotId,
            });
          }
          return;
        }
        onAppointmentClick({ calendarEvent: eventBase, isPastEvent });
      },
      selectable: false,
      backgroundEvents: offlineEvents,
      date: dateLocal,
      getNow,
    };
  }, [date, view, store, workingHours, allServices, offlineEvents, calendarData, notLoading, peakTimes]);

  if (appointmentsState.error) {
    return <p>{intl.formatMessage({ id: 'common.error' })}</p>;
  }

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" padding="16px 18px">
        <Stack direction="column" className={styles.calendarContainer}>
          <Typography className={styles.currentDay}>{date.locale(localeContext?.locale || 'en').format('D. MMM, dddd')}</Typography>
          <Stack direction="row" alignItems="center">
            <DatePicker date={dateString} setDate={onDateChange} view={view} timezone={store.timezone} />

            {calendarData.resources.length > 0 && daySummary && (
              <DayOverview
                daySummary={daySummary}
                onSeeMore={onShowCalendarOverviewClick}
                isReportEnabled={isReportEnabled}
                onSeeReport={onShowReportClick}
              />
            )}
          </Stack>
        </Stack>

        <Stack direction="row" className={styles.actionsContainer} alignItems="center">
          <IconButton onClick={onToggleFilters} className={styles.filtersIcon}>
            <Badge classes={{ dot: clsx(styles.filtersBadge, isFiltersActive && styles.activeFiltersBadge) }} variant="dot">
              <FilterIcon />
            </Badge>
          </IconButton>
          <IconButton onClick={onToggleSearch} className={styles.searchIcon}>
            <SearchIcon height={16} width={24} />
          </IconButton>
          <Stack direction="row" alignItems="center" spacing={1}>
            {isSeriesEnabled && <SeriesButton />}
            <CreateAppointmentButton onClick={() => onCreateAppointmentClick()} />
          </Stack>
        </Stack>
      </Stack>

      <Box className={styles.calendarWrapper}>
        {!workingHours.isLoading &&
          (workingHours.isOpen ? (
            <>
              <ReactCalendar {...config} events={calendarData.events} resources={calendarData.resources} showMultiDayTimes />
              <CalendarFooter roomsAmount={roomsAmount} />
            </>
          ) : (
            intl.formatMessage({ id: 'calendar.storeNotWorking' })
          ))}
      </Box>
      {isFiltersOpened && (
        <AppointmentsFilters
          isOpen={isFiltersOpened}
          onClose={onToggleFilters}
          onSave={onSaveFilters}
          allServices={allServices}
          eventTypeCounters={calendarData.eventTypeCounters}
          values={filters}
          defaultValues={defaultFilters}
        />
      )}
      <NoticeStack>
        <Notice
          open={deletionWarningState === 'visible'}
          onClose={onDeletionWarningClose}
          severity="warning"
          autoHideDuration={60000}
          title={intl.formatMessage({ id: 'calendar.appointmentIsDeleting' })}
          description={intl.formatMessage({ id: 'calendar.appointmentIsDeletingDescription' })}
        />
        <Notice
          open={deletionErrorState}
          onClose={onDeletionErrorClose}
          severity="error"
          autoHideDuration={60000}
          title={intl.formatMessage({ id: 'common.deleteAppointmentError' })}
          description={intl.formatMessage({ id: 'common.deleteAppointmentErrorDescription' })}
        />
        <Notice
          open={slotsErrorShown}
          onClose={() => setSlotsErrorShown(false)}
          severity="error"
          autoHideDuration={60000}
          title={intl.formatMessage({ id: 'slot.slotsFetchErrorTitle' })}
        />
      </NoticeStack>
    </>
  );
}
